import {
  Injectable,
  ApplicationRef,
  Injector,
  Type,
  ViewContainerRef,
  ComponentRef,
} from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private dialogContainer: HTMLElement | null = null;
  private viewContainerRef: ViewContainerRef | null = null;
  private lastComponentRef: ComponentRef<any> | null = null;
  private debugMode: boolean = false; // Set to true for debugging

  constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
  ) {}

  // Enable/disable debug mode for logging click events
  setDebugMode(enabled: boolean) {
    this.debugMode = enabled;
  }

  // Set ViewContainerRef
  setViewContainerRef(vcr: ViewContainerRef) {
    this.viewContainerRef = vcr;
  }

  // Get the last ComponentRef
  getLastComponentRef(): ComponentRef<any> | null {
    return this.lastComponentRef;
  }

  open<T>(config: {
    component: Type<T>;
    data?: any;
    confirmText?: string;
    cancelText?: string;
    allowBackdropDismiss?: boolean;
    isAlert?: boolean;
    confirmBtnClass?: string;
    cancelBtnClass?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    onBackdropDismiss?: () => void;
  }): ComponentRef<T> | null {
    if (!this.viewContainerRef) {
      console.error(
        'ViewContainerRef not set. Please call setViewContainerRef first.',
      );
      return null;
    }

    // Default configuration
    const defaults = {
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      allowBackdropDismiss: true,
      isAlert: false,
      confirmBtnClass: 'btn btn-sm btn-primary',
      cancelBtnClass: 'btn btn-sm btn-secondary',
      onConfirm: () => {},
      onCancel: () => {},
      onBackdropDismiss: () => {},
    };
    const dialogConfig = { ...defaults, ...config };

    // Create dialog container
    this.dialogContainer = document.createElement('div');
    this.dialogContainer.className = 'breath-modal-container';
    document.body.appendChild(this.dialogContainer);

    // Create backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'breath-modal-backdrop';
    if (dialogConfig.allowBackdropDismiss) {
      backdrop.addEventListener('click', (event: Event) => {
        if (this.debugMode) {
        }
        dialogConfig.onBackdropDismiss();
        this.close(componentRef);
      });
    }

    // Create dialog wrapper
    const dialogWrapper = document.createElement('div');
    dialogWrapper.className = 'breath-modal-dialog';
    // Prevent click events from bubbling to backdrop
    dialogWrapper.addEventListener('click', (event: Event) => {
      if (this.debugMode) {
      }
      event.stopPropagation();
      event.preventDefault(); // Additional safeguard
    });

    // Create component
    const componentRef = this.viewContainerRef.createComponent(
      dialogConfig.component,
      { injector: this.injector },
    );
    this.lastComponentRef = componentRef;

    // Pass data to component
    if (dialogConfig.data) {
      Object.assign(componentRef.instance, dialogConfig.data);
    }

    // Create button container
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'breath-modal-buttons';

    // Create confirm button
    const confirmButton = document.createElement('button');
    confirmButton.textContent = dialogConfig.confirmText;
    confirmButton.className = dialogConfig.isAlert
      ? dialogConfig.cancelBtnClass
      : dialogConfig.confirmBtnClass;
    confirmButton.addEventListener('click', (event: Event) => {
      if (this.debugMode) {
      }
      event.stopPropagation(); // Ensure button clicks don't bubble678
      dialogConfig.onConfirm();
      this.close(componentRef);
    });

    // Create cancel button
    const cancelButton = document.createElement('button');
    cancelButton.textContent = dialogConfig.cancelText;
    cancelButton.className = dialogConfig.cancelBtnClass;
    cancelButton.addEventListener('click', (event: Event) => {
      if (this.debugMode) {
      }
      event.stopPropagation(); // Ensure button clicks don't bubble
      dialogConfig.onCancel();
      this.close(componentRef);
    });

    // Append elements in correct order
    if (!dialogConfig.isAlert) {
      buttonContainer.appendChild(cancelButton);
    }
    buttonContainer.appendChild(confirmButton);

    dialogWrapper.appendChild(componentRef.location.nativeElement);
    dialogWrapper.appendChild(buttonContainer);
    this.dialogContainer.appendChild(backdrop);
    this.dialogContainer.appendChild(dialogWrapper);

    return componentRef;
  }

  private close<T>(componentRef: ComponentRef<T>): void {
    if (this.dialogContainer) {
      componentRef.destroy();
      this.lastComponentRef = null;
      document.body.removeChild(this.dialogContainer);
      this.dialogContainer = null;
    }
  }
}
